import React, { useState, useEffect } from "react";
import { Button, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useGetStaffEmailListQuery } from "../../service/apiSlices/workbench";
import useDebounce from "../../hooks/useDebounce";

interface EmailInputProps {
    onInvite: (newEmails: string[]) => void;
    visible?: boolean;
}

const EmailInput: React.FC<EmailInputProps> = ({ onInvite, visible }) => {
    const { t } = useTranslation("common");
    const [searchValue, setSearchValue] = useState<string>("");
    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
    const debouncedSearchValue = useDebounce(searchValue, 500);

    // Fetch staff email list based on debounced search value
    const { data: staffEmailList, isFetching } = useGetStaffEmailListQuery(debouncedSearchValue, {
        skip: !visible,
    });

    // Map API response to Select options
    const emailOptions = staffEmailList?.data?.map((staff: any) => ({
        label: staff.email,
        value: staff.email,
    })) || [];

    // Handle search input change
    const handleSearch = (value: string) => {
        setSearchValue(value);
    };

    // Handle selection change
    const handleSelectChange = (values: string[]) => {
        setSelectedEmails(values);
    };

    // Handle invite action
    const handleInviteClick = () => {
        if (selectedEmails.length > 0) {
            onInvite(selectedEmails);
            setSelectedEmails([]);
            setSearchValue("");
        }
    };

    // Clear search value when selection changes to prevent stale search results
    useEffect(() => {
        if (selectedEmails.length === 0) {
            setSearchValue("");
        }
    }, [selectedEmails]);

    return (
        <div className="search-input-wrapper">
            <label className="invite-team-workbench-modal-email-label">
                {t("inviteTeamWorkbenchModal.add")}
            </label>
            <div className="d-flex justify-content-between align-items-center gap-2">
                <Select
                    id="email-select"
                    mode="multiple"
                    placeholder={t("inviteTeamWorkbenchModal.emailPlaceholder")}
                    value={selectedEmails}
                    onSearch={handleSearch}
                    onChange={handleSelectChange}
                    options={emailOptions}
                    showSearch
                    allowClear
                    filterOption={false} // Disable local filtering since we use API
                    loading={isFetching}
                    className="invite-team-workbench-modal-input"
                    style={{ width: "100%" }}
                />
                <Button
                    id="invite-button"
                    onClick={handleInviteClick}
                    className="invite-team-workbench-modal-button"
                    disabled={selectedEmails.length === 0}
                >
                    {t("inviteTeamWorkbenchModal.buttonText")}
                </Button>
            </div>
        </div>
    );
};

export default React.memo(EmailInput);